import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, TablePagination, TextField, MenuItem, Chip, IconButton,
  Grid, Card, CardContent, Tooltip, CircularProgress, Alert
} from '@mui/material';
import {
  Refresh as RefreshIcon, DeleteSweep as DeleteIcon,
  Search as SearchIcon, FilterList as FilterIcon
} from '@mui/icons-material';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const actionColors = {
  CREATE: '#10b981', UPDATE: '#3b82f6', DELETE: '#ef4444',
  LOGIN: '#8b5cf6', LOGOUT: '#6b7280', EXPORT: '#f59e0b',
  IMPORT: '#06b6d4', ARCHIVE: '#f97316', RESTORE: '#14b8a6',
  BACKUP: '#0ea5e9', VIEW: '#9ca3af', OTHER: '#6b7280'
};

const API = '/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(50);
  const [actionFilter, setActionFilter] = useState('');
  const [resourceFilter, setResourceFilter] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [actionStats, setActionStats] = useState([]);
  const [message, setMessage] = useState(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: page + 1, limit };
      if (actionFilter) params.action = actionFilter;
      if (resourceFilter) params.resource = resourceFilter;
      if (search) params.search = search;
      const res = await axios.get(`${API}/audit-logs`, { params, headers: authHeaders() });
      if (res.data.success) {
        setLogs(res.data.data);
        setTotal(res.data.total);
        setActionStats(res.data.actionStats || []);
      }
    } catch (err) {
      console.error('Erreur chargement audit logs:', err);
      setMessage({ type: 'error', text: err.response?.data?.message || err.message || 'Erreur chargement' });
    }
    setLoading(false);
  }, [page, limit, actionFilter, resourceFilter, search]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handleCleanOld = async () => {
    if (!window.confirm('Supprimer les logs de plus d\'un an ?')) return;
    try {
      await axios.delete(`${API}/audit-logs/old?days=365`, { headers: authHeaders() });
      fetchLogs();
    } catch (err) {
      console.error(err);
    }
  };

  const formatDate = (date) => {
    try { return format(new Date(date), 'dd/MM/yyyy HH:mm', { locale: fr }); }
    catch { return '-'; }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ color: '#1f2937' }}>Journal d'audit</Typography>
          <Typography variant="body2" color="text.secondary">{total} entrées</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Rafraîchir"><IconButton onClick={fetchLogs}><RefreshIcon /></IconButton></Tooltip>
          <Tooltip title="Nettoyer les logs > 1 an">
            <IconButton onClick={handleCleanOld} color="error"><DeleteIcon /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      {message && <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {actionStats.slice(0, 6).map((s, i) => (
          <Grid item xs={6} sm={4} md={2} key={i}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              <CardContent sx={{ p: 2, textAlign: 'center' }}>
                <Chip label={s._id} size="small" sx={{ bgcolor: `${actionColors[s._id] || '#6b7280'}20`, color: actionColors[s._id] || '#6b7280', fontWeight: 600, mb: 0.5, fontSize: '0.65rem' }} />
                <Typography variant="h6" fontWeight="bold">{s.count}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" placeholder="Rechercher..." value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
              InputProps={{ startAdornment: <SearchIcon sx={{ color: '#9ca3af', mr: 1, fontSize: 20 }} /> }} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField select fullWidth size="small" label="Action" value={actionFilter} onChange={e => { setActionFilter(e.target.value); setPage(0); }}>
              <MenuItem value="">Toutes</MenuItem>
              {Object.keys(actionColors).map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth size="small" label="Ressource" value={resourceFilter} onChange={e => { setResourceFilter(e.target.value); setPage(0); }} placeholder="Ex: ETUDIANT" />
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f9fafb' }}>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Utilisateur</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Rôle</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Action</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Ressource</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Détails</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
            ) : logs.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: '#9ca3af' }}>Aucune entrée</TableCell></TableRow>
            ) : logs.map((log) => (
              <TableRow key={log._id} hover sx={{ '&:hover': { bgcolor: '#f9fafb' } }}>
                <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{formatDate(log.createdAt)}</TableCell>
                <TableCell sx={{ fontSize: '0.75rem' }}>{log.userName || '-'}</TableCell>
                <TableCell><Chip label={log.userRole || '-'} size="small" sx={{ fontSize: '0.65rem', height: 22 }} /></TableCell>
                <TableCell>
                  <Chip label={log.action} size="small" sx={{
                    bgcolor: `${actionColors[log.action] || '#6b7280'}20`,
                    color: actionColors[log.action] || '#6b7280',
                    fontWeight: 600, fontSize: '0.65rem', height: 22
                  }} />
                </TableCell>
                <TableCell sx={{ fontSize: '0.75rem' }}>{log.resource}{log.resourceId ? ` #${log.resourceId.slice(-6)}` : ''}</TableCell>
                <TableCell sx={{ fontSize: '0.7rem', color: '#6b7280', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {log.details || '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination component="div" count={total} page={page} onPageChange={(e, p) => setPage(p)}
          rowsPerPage={limit} onRowsPerPageChange={e => { setLimit(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100]} labelRowsPerPage="Par page" />
      </TableContainer>
    </Box>
  );
};

export default AuditLogs;


